/**
 * 模組頁面代碼 : 1320-UI-04
 * 頁面說明 : 角色維護列表畫面
 * @module web/modules/role-permission/components/RoleListModal
 */


// ----- import from npm modules ----
import $ from 'jquery';
import React, { useEffect } from 'react';

// ----- import from local modules -----
import {
    AccessModeEnums,
    ModuleEnums,
} from '../../../components/BaseComponent';
import {
    DataTableComponent,
} from '../../../components/CommonComponent';
import { AlertModalModeEnums } from 'web/components/AlertModal';

const RoleListModal = React.forwardRef((props, ref) => {
    const { checkPermission, onNewRole, onEditRole, onEditRoleStatus, roleList, needRoleListLoad, setNeedRoleListLoad } = props;

    const prepareRoleListTable = () => {
        // 定義資料內容
        const dataSet = roleList.map((role) => {
            return [
                role.roleName,
                role.roleDescription,
                role.flgStatus,
            ];
        });

        // 定義欄位屬性
        const columnConfig = [
            { title: '角色名稱', data: 0, width: '30%' },
            { title: '角色說明', data: 1, width: '50%' },
            {
                orderable: false,
                order: null,
                title: '啟用',
                data: 2,
                className: 'dt-center',
                render: (data, type, row, meta) => {
                    let enableId = 'eab-role-' + meta.row;
                    let isChecked = data === 'A' ? 'checked' : '';
                    let enableButton = checkPermission(ModuleEnums.ROLE, AccessModeEnums.DELETE) ? '' : 'disabled';
                    return `<div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" role="switch" id="${enableId}" ${isChecked} ${enableButton} />
                                 <label class="form-check-label" for="${enableId}"></label>
                            </div>`;
                },
            },
            {
                orderable: false,
                order: null,
                data: null,
                className: 'dt-right',
                render: (data, type, row, meta) => {
                    let editId = 'edit-role-' + meta.row;
                    let editButton = checkPermission(ModuleEnums.ROLE, AccessModeEnums.UPDATE) ?
                        `<button type="button" class="btn btn-sm btn-link" id="${editId}">
                            <svg class="btn__icon btn__icon--sm me-1" aria-hidden="true">
                                <use href="#ico-edit"></use>
                            </svg>
                            修改
                        </button>` : '';
                    return `<div class="btn-group" role="group" aria-label="">
                                ${editButton}
                            </div>`;
                },
                width: '20%',
            },
        ];

        // 定義排序設定
        const orderConfig = {
            idx: 0,
            dir: 'asc',
        };

        // 定義固定欄位設定
        const fixedColumnsConfig = {
            start: 1,
        };

        // 先清除datatable
        if ($.fn.DataTable.isDataTable('#datatable__roleList')) {
            $('#datatable__roleList').empty();
            $('#datatable__roleList').DataTable().destroy();
        }
        
        return DataTableComponent('#datatable__roleList', dataSet, columnConfig, orderConfig, fixedColumnsConfig);
    };

    // 當表格的容器可見後，調整列寬
    const adjustTableWhenVisible = () => {
        const tableContainer = document.querySelector('#datatable__roleList'); // 假設這是表格的容器
        if (tableContainer && tableContainer.offsetParent !== null) {
            let t = prepareRoleListTable();
            t.columns.adjust().draw();
        } else {
            setTimeout(adjustTableWhenVisible, 100); // 容器不可見，稍後再嘗試
        }
    };

    useEffect(() => {
        if (needRoleListLoad) {
            setNeedRoleListLoad(false);

            const table = document.querySelector('#datatable__roleList');
            const maskElement = document.querySelector('#modal-mask');
            // 顯示遮罩
            maskElement.style.opacity = '0.5';

            // 重新繪製表格
            adjustTableWhenVisible();

            // 隱藏遮罩
            maskElement.style.opacity = '0'
            setTimeout(() => {
                maskElement.style.display = 'none'
            }, 200);

            if (ref.current) {
                ref.current.addEventListener('click', function (event) {
                    const button = event.target.closest('.btn');
                    if (button && button.id.startsWith('edit-role-')) {
                        onEditRole(parseInt(button.id.split('-')[2]));
                    }
                });
                ref.current.addEventListener('change', function (event) {
                    const checkbox = event.target.closest('input[type="checkbox"]');
                    if (checkbox && checkbox.id.startsWith('eab-role-')) {
                        const index = parseInt(checkbox.id.split('-')[2]);
                        const mode = checkbox.checked ? AlertModalModeEnums.ENABLE : AlertModalModeEnums.DISABLE;
                        let updateRole = roleList[index];
                        if (updateRole) {
                            let message = '';
                            if (mode === AlertModalModeEnums.ENABLE) {
                                message = `啟用 [ ${updateRole.roleName} ]`;
                            } else {
                                message = `停用 [ ${updateRole.roleName} ]`;
                            }
                            onEditRoleStatus(mode, message, updateRole.roleId);
                        } else {
                            console.log('No role found at index:', index);
                        }
                    }
                });
            }
        }
    }, [needRoleListLoad]);

    return (
        <div ref={ref}
            className="modal fade"
            id="modalRoleList"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="modalRoleListLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal__form modal-content needs-validation" id="modalRoleListForm">
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="modalRoleListLabel">
                            <div className="modal__title__badge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href="#ico-users-edit"></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">角色維護</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div id="modal-mask" className="modal-mask"></div>
                        <div className="datatable__tool mb-2">
                            <div className="d-flex align-items-center gap-2 ms-auto">
                                {checkPermission(ModuleEnums.ROLE, AccessModeEnums.CREATE) && (
                                <button type="button" className="btn btn-sm btn-primary" id="modalRoleBtnNew" onClick={onNewRole}>
                                    <svg className="btn__icon me-2" aria-hidden="true">
                                        <use href="#ico-plus"></use>
                                    </svg>
                                    新增
                                </button>
                                )}
                            </div>
                        </div>
                        <div className="datatable__data">
                            <table id="datatable__roleList" className="display table table-hover align-middle nowrap" style={{ width: '100%' }}></table>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default RoleListModal;